import React, { useState, useEffect } from 'react';
import useVersionedApi from '../hooks/useVersionedApi';
import { ApiVersion } from '../api/versionedClient';

/**
 * A component that allows selecting the API version
 * Shows warnings when missing endpoints are detected for a version
 */
const ApiVersionSelector: React.FC = () => {
  const { apiVersion, setApiVersion } = useVersionedApi();
  const [hasMissingEndpoints, setHasMissingEndpoints] = useState<Record<string, boolean>>({
    db: false,
    v2: false
  });
  
  useEffect(() => {
    // Check for missing endpoints periodically
    const intervalId = setInterval(() => {
      if (window.missingDbEndpoints) {
        const dbMissing = window.missingDbEndpoints.some(e => e.version === 'db');
        const v2Missing = window.missingDbEndpoints.some(e => e.version === 'v2');
        
        setHasMissingEndpoints({
          db: dbMissing,
          v2: v2Missing
        });
      }
    }, 1000);
    
    return () => clearInterval(intervalId);
  }, []);
  
  const handleVersionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newVersion = event.target.value as ApiVersion;
    setApiVersion(newVersion);
  };
  
  return (
    <div className="flex items-center space-x-2">
      <label htmlFor="api-version" className="text-sm font-medium whitespace-nowrap">
        API Version:
      </label>
      <div className="relative">
        <select
          id="api-version"
          value={apiVersion}
          onChange={handleVersionChange}
          className={`
            text-sm py-1 px-2 border rounded
            ${hasMissingEndpoints[apiVersion] ? 'border-red-500' : 'border-gray-300'}
          `}
        >
          <option value="v1">v1 (Blob Storage)</option>
          <option value="db">
            DB 
            {hasMissingEndpoints.db && " ⚠️"}
          </option>
          <option value="v2">
            v2 
            {hasMissingEndpoints.v2 && " ⚠️"}
          </option>
        </select>
        
        {hasMissingEndpoints[apiVersion] && (
          <div className="absolute right-0 -top-1 -mr-1 -mt-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
        )}
      </div>
      
      {hasMissingEndpoints[apiVersion] && (
        <div className="text-xs text-red-500">
          Missing endpoints detected
        </div>
      )}
    </div>
  );
};

export default ApiVersionSelector; 